import { History } from 'history';
import isBrowser from 'is-browser';
import queryString from 'query-string';

/**
 * Get the correct format for the search from router history
 * Removes -app suffix from parameter names (keys) only, not from values
 * @param history Router history
 */
export const getHistorySearch = (history: History): string => {
  const search = history.location.search;

  const query = queryString.parse(decodeURI(search));
  delete query['---'];

  // Remove -app suffix from parameter names only, preserve values
  const normalizedQuery: Record<string, any> = {};
  for (const [key, value] of Object.entries(query)) {
    const normalizedKey = key.replace(/-app$/i, '');
    normalizedQuery[normalizedKey] = value;
  }

  const formattedSearch =
    Object.keys(normalizedQuery).length === 0
      ? ''
      : `?${queryString.stringify(normalizedQuery)}`;

  return formattedSearch;
};

/**
 * Push to history with pathname and search params
 * This is critical for AEM - always include pathname to prevent AEM from re-resolving the page
 * @param history React history
 * @param pathname The pathname to navigate to
 * @param search The search query string (without ?)
 */
export const pushWithPathname = (
  history: History,
  pathname: string,
  search: string = ''
): void => {
  const path = search ? `${pathname}?${search}` : pathname;
  history.push(path);
};

/**
 * Replace in history with pathname and search params
 * This is critical for AEM - always include pathname to prevent AEM from re-resolving the page
 * @param history React history
 * @param pathname The pathname to navigate to
 * @param search The search query string (without ?)
 */
export const replaceWithPathname = (
  history: History,
  pathname: string,
  search: string = ''
): void => {
  const path = search ? `${pathname}?${search}` : pathname;
  history.replace(path);
};

/**
 * Add a query parameter to the url using push
 * Always preserves pathname for AEM compatibility
 * @param history React history
 * @param key Key for the query parameter in the url
 * @param value Value for the query parameter in the url
 */
export const addUrlParamWithPush = (
  history: History,
  key: string,
  value: string | number
): void => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = { ...query, [key]: value };
  const path = `${history.location.pathname}?${queryString.stringify(param)}`;
  history.push(path);
};

/**
 * Add a query parameter to the url using replace
 * Always preserves pathname for AEM compatibility
 * @param history React history
 * @param key Key for the query parameter in the url
 * @param value Value for the query parameter in the url
 */
export const addUrlParam = (
  history: History,
  key: string,
  value: string
): void => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = { ...query, [key]: value };
  const path = `${history.location.pathname}?${queryString.stringify(param)}`;
  history.replace(path);
};

/**
 * Delete a query parameter from the url
 * Always preserves pathname for AEM compatibility
 * @param history React history
 * @param key Key for the query parameter to delete
 */
export const deleteUrlParam = (history: History, key: string): void => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));
  const param = Object.fromEntries(
    Object.entries(query).filter(([k]) => k !== key)
  );
  const searchString =
    Object.keys(param).length > 0 ? `?${queryString.stringify(param)}` : '';
  const path = `${history.location.pathname}${searchString}`;
  history.replace(path);
};

/**
 * Push multiple URL params at once (batched) - creates single history entry
 * Similar to inventory-fa's approach but batches multiple changes
 * @param history React history
 * @param paramsToSet Object with params to set (undefined/empty string = delete)
 */
export const pushMultipleUrlParams = (
  history: History,
  paramsToSet: Record<string, string | number | undefined>
): void => {
  const search = getHistorySearch(history);
  const query = queryString.parse(decodeURI(search));

  // Update query with new params
  for (const [key, value] of Object.entries(paramsToSet)) {
    if (value === undefined || value === '') {
      delete query[key];
    } else {
      query[key] = value.toString();
    }
  }

  const searchString =
    Object.keys(query).length > 0 ? queryString.stringify(query) : '';

  const path = searchString
    ? `${history.location.pathname}?${searchString}`
    : history.location.pathname;

  history.push(path);
};

/**
 * Push to history with updated search params, preserving pathname
 * This is the main utility for updating query params in AEM
 * @param history React history
 * @param searchParams The search params object to set
 */
export const pushSearchParams = (
  history: History,
  searchParams: Record<string, string | number | undefined>
): void => {
  // Filter out undefined values
  const filteredParams = Object.fromEntries(
    Object.entries(searchParams).filter(
      ([_, value]) => value !== undefined && value !== ''
    )
  ) as Record<string, string | number>;

  const searchString =
    Object.keys(filteredParams).length > 0
      ? queryString.stringify(filteredParams)
      : '';

  pushWithPathname(history, history.location.pathname, searchString);
};

/**
 * Replace in history with updated search params, preserving pathname
 * @param history React history
 * @param searchParams The search params object to set
 */
export const replaceSearchParams = (
  history: History,
  searchParams: Record<string, string | number | undefined>
): void => {
  // Filter out undefined values
  const filteredParams = Object.fromEntries(
    Object.entries(searchParams).filter(
      ([_, value]) => value !== undefined && value !== ''
    )
  ) as Record<string, string | number>;

  const searchString =
    Object.keys(filteredParams).length > 0
      ? queryString.stringify(filteredParams)
      : '';

  replaceWithPathname(history, history.location.pathname, searchString);
};
/**
 * Extract base path from serverRequestUrl (without /__app/)
 * Example: "https://cs-int-vw-us.lighthouselabs.eu/en/quick-start-guides-ssr.html/__app/2025/atlas.app"
 * Returns: "/en/quick-start-guides-ssr.html"
 * 
 * Example: "https://cs-int-vw-us.lighthouselabs.eu/en/quick-start-guides-ssr.html"
 * Returns: "/en/quick-start-guides-ssr.html"
 */
function extractBasePathFromServerRequestUrl(serverRequestUrl: string): string {
  try {
    const url = new URL(serverRequestUrl);
    const pathname = url.pathname;
    
    // Check if URL has /__app/ in it
    const hasAppSegment = pathname.includes('/__app/');
    
    if (hasAppSegment) {
      // Find everything up to but not including /__app/
      // Pathname format: /en/quick-start-guides-ssr.html/__app/2025/atlas.app
      const match = pathname.match(/^(.+)\/__app\//);
      if (match) {
        return match[1];
      }
    }
    
    // If no /__app/ found, check if it's a .html page (base page)
    // This handles the case where we're on the base page without /__app/
    if (!hasAppSegment && pathname.includes('.html')) {
      // Return the pathname as-is (e.g., /en/quick-start-guides-ssr.html)
      return pathname;
    }
    
    // Fallback: if no /__app/ and no .html, return the pathname without .app and last segments
    const withoutExt = pathname.replace(/\.app$/, '');
    const parts = withoutExt.split('/').filter(Boolean);
    // Keep everything except the last 2 parts (modelYear, modelId)
    if (parts.length >= 2) {
      const baseParts = parts.slice(0, -2);
      return '/' + baseParts.join('/');
    }
    
    return pathname;
  } catch (e) {
    // If URL parsing fails, return empty string
    return '';
  }
}

const testIntegrationSSR = false;
const mockIntegratorServerRequestUrl ='https://cs-int-vw-us.lighthouselabs.eu/en/quick-start-guides-ssr.html/__app/2025/atlas.app';
//"https://cs-int-vw-us.lighthouselabs.eu/en/quick-start-guides-ssr.html"

/**
 * Create an href URL for feature-hub navigation
 *
 * In SSR integrator: Returns relative path with base path from server-request (e.g., "/en/quick-start-guides-ssr.html/__app/2025/atlas/section.app")
 * In local SSR/client-side: Returns feature-hub query parameter URL (e.g., "/?-={...}") for client-side routing
 *
 * @param history React history
 * @param featureAppRoute The route path for the feature app (e.g., "/2025/atlas/section")
 * @param serverRequestUrl Optional server request URL to extract base path for SSR integrator
 * @param featureAppName The name of the feature app (default: "quick-start-guide")
 * @returns A full href URL appropriate for the environment
 */
export const createFeatureHubHref = (
  history: History,
  featureAppRoute: string,
  serverRequestUrl?: string,
  featureAppName: string = 'quick-start-guide'
): string | undefined=> {
  const isSSR = !isBrowser || typeof window === 'undefined';
  //const isLocalSSR = isSSR && !isIntegrationEnv;

  if (!isSSR && !serverRequestUrl) 
    serverRequestUrl = window.location.origin + window.location.pathname;
  if (testIntegrationSSR) serverRequestUrl = mockIntegratorServerRequestUrl;

  const isIntegrationEnv = serverRequestUrl?.includes('__app') || serverRequestUrl?.includes('.html')
  
  // Extract query params from featureAppRoute if they exist
  const [routePath, routeQueryString] = featureAppRoute.split('?');
  const routeQueryParams = routeQueryString ? queryString.parse(routeQueryString) : {};

  // In integrator environment (has serverRequestUrl), build relative path with base
  if (isIntegrationEnv) {
    if (!serverRequestUrl || serverRequestUrl === '') return undefined;

    const basePath = extractBasePathFromServerRequestUrl(serverRequestUrl);
    
    // If route is just "/", return the base path without /__app/ or .app
    if (routePath === '/' || routePath === '') {
      // If there are query params, append them with -app suffix
      if (Object.keys(routeQueryParams).length > 0) {
        const queryParamsWithSuffix: Record<string, string> = {};
        for (const [key, value] of Object.entries(routeQueryParams)) {
          queryParamsWithSuffix[`${key}-app`] = value as string;
        }
        const searchString = queryString.stringify(queryParamsWithSuffix);
        return `${basePath}?${searchString}`;
      }
      return basePath;
    }

    // Remove leading slash from routePath
    const cleanRoutePath = routePath.startsWith('/')
      ? routePath.substring(1)
      : routePath;

    // Build the path with .app extension
    let result = `${basePath}/__app/${cleanRoutePath}.app`;

    // Append query params with -app suffix if they exist
    if (Object.keys(routeQueryParams).length > 0) {
      const queryParamsWithSuffix: Record<string, string> = {};
      for (const [key, value] of Object.entries(routeQueryParams)) {
        queryParamsWithSuffix[`${key}-app`] = value as string;
      }
      const searchString = queryString.stringify(queryParamsWithSuffix);
      result = `${result}?${searchString}`;
    }

    return result;
  }

  // In client-side or local SSR, use feature-hub query parameter routing
  const search = history.location.search;
  // Parse query string - queryString.parse handles URL decoding automatically
  const query = queryString.parse(search);

  // Remove query params that are already in the route to avoid duplication
  const cleanedQuery = { ...query };
  for (const key of Object.keys(routeQueryParams)) {
    delete cleanedQuery[key];
  }

  // Parse the `-` parameter which contains JSON-encoded feature app routes
  // The value might be URL-encoded, so we need to decode it first
  let featureAppRoutes: Record<string, string> = {};
  if (cleanedQuery['-']) {
    try {
      const value = cleanedQuery['-'] as string;
      // Try to parse directly first (in case it's already decoded)
      try {
        featureAppRoutes = JSON.parse(value);
      } catch {
        // If that fails, try decoding URL encoding first
        const decoded = decodeURIComponent(value);
        featureAppRoutes = JSON.parse(decoded);
      }
    } catch (e) {
      // If parsing fails, start with empty object
      featureAppRoutes = {};
    }
  }

  // Update the feature app route - include the full route with query params in the JSON
  // This way query params are stored in the JSON, not as separate URL params
  featureAppRoutes[featureAppName] = featureAppRoute;

  // Create new query params with updated `-` parameter
  const updatedQuery = { ...cleanedQuery };
  // JSON.stringify will create the JSON, queryString.stringify will URL-encode it
  updatedQuery['-'] = JSON.stringify(featureAppRoutes);

  // Build the search string - queryString.stringify will URL-encode the value
  const searchString = queryString.stringify(updatedQuery);

  // Feature-hub routing is handled entirely through the `-` query parameter
  // The pathname should be the base path (/) to avoid conflicts
  return `/?${searchString}`;
};
